import React, { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { usersAPI, conversationsAPI } from '../services/api'
import PublicHeader from '../components/PublicHeader'
import {
  ArrowLeft, MapPin, Building, Calendar,
  MessageCircle, Globe, Users, ExternalLink,
  Instagram, Youtube, Twitter, Video
} from 'lucide-react'

// location komt uit de API als string óf als { city, country } — altijd naar tekst omzetten (anders React error #31)
const formatLocation = (loc) => {
  if (!loc) return ''
  if (typeof loc === 'string') return loc
  if (typeof loc === 'object') {
    return [loc.city, loc.region, loc.country].filter(v => typeof v === 'string' && v.trim()).join(', ')
  }
  return String(loc)
}

const CompanyDetailPage = () => {
  const { id } = useParams()
  const { user, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [company, setCompany] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [startingChat, setStartingChat] = useState(false)

  useEffect(() => {
    fetchCompany()
  }, [id])

  const fetchCompany = async () => {
    try {
      setLoading(true)
      const response = await usersAPI.getCompany(id)
      setCompany(response.data.company || response.data)
    } catch (error) {
      console.error('Error fetching company:', error)
      setError('Bedrijf niet gevonden')
    } finally {
      setLoading(false)
    }
  }

  const handleStartChat = async () => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }

    try {
      setStartingChat(true)
      const response = await conversationsAPI.createConversation({
        participantId: id
      })

      const conversationId = response.data.conversation?._id || response.data._id
      navigate(`/messages?conversation=${conversationId}`)
    } catch (error) {
      console.error('Error starting chat:', error)
      alert('Er is een fout opgetreden bij het starten van een gesprek')
    } finally {
      setStartingChat(false)
    }
  }

  const getSocialIcon = (platform) => {
    const icons = {
      instagram: Instagram,
      youtube: Youtube,
      tiktok: Video,
      twitter: Twitter,
      website: Globe
    }
    return icons[platform] || Globe
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return ''
    const date = new Date(dateStr)
    return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#3EB489] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#475569]">Bedrijf laden...</p>
        </div>
      </div>
    )
  }

  if (error || !company) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Bedrijf niet gevonden</h2>
          <p className="text-[#475569] mb-6">{error}</p>
          <Link to="/companies" className="text-[#3EB489] hover:underline">
            Terug naar bedrijven
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A]">
      {/* Header */}
      <PublicHeader />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-6 md:pt-10 pb-20">
        {/* Back Button */}
        <Link to="/companies" className="inline-flex items-center min-h-[44px] text-[#475569] hover:text-[#3EB489] transition mb-4 md:mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Terug naar bedrijven
        </Link>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Company Header */}
            <div className="relative">
              <div className="relative p-5 md:p-8 rounded-3xl bg-white border border-gray-200 shadow-md">
                <div className="flex flex-col sm:flex-row items-start gap-5 sm:gap-6">
                  <div className="flex-shrink-0">
                    {company.logo ? (
                      <img
                        src={company.logo}
                        alt={company.name}
                        className="w-24 h-24 rounded-full object-cover ring-4 ring-[#3EB489]/20"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#3EB489] to-[#2A7E5F] flex items-center justify-center ring-4 ring-[#3EB489]/20">
                        <span className="text-white text-3xl font-bold">
                          {(company.name || 'B').charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0 w-full">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                      <h1 className="text-2xl md:text-3xl font-bold break-words">
                        {company.name || 'Naamloos Bedrijf'}
                      </h1>
                      {company.role === 'agency' && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                          Agency
                        </span>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-x-4 gap-y-2 mb-4">
                      {formatLocation(company.location) && (
                        <div className="flex items-center gap-2 text-[#475569]">
                          <MapPin className="w-4 h-4 flex-shrink-0" />
                          <span>{formatLocation(company.location)}</span>
                        </div>
                      )}
                      {company.companySize && (
                        <div className="flex items-center gap-2 text-[#475569]">
                          <Users className="w-4 h-4" />
                          <span>{company.companySize} medewerkers</span>
                        </div>
                      )}
                      {company.joinedAt && (
                        <div className="flex items-center gap-2 text-[#475569]">
                          <Calendar className="w-4 h-4" />
                          <span>Lid sinds {formatDate(company.joinedAt)}</span>
                        </div>
                      )}
                    </div>

                    {company.industry && (
                      <span className="inline-flex items-center px-3 py-1 bg-white border border-[#3EB489]/40 text-[#2A7E5F] rounded-full text-sm mr-2">
                        {company.industry}
                      </span>
                    )}

                    {company.specializations && company.specializations.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {company.specializations.map((spec, index) => (
                          <span key={index} className="px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-sm">
                            {spec}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Bio */}
            {company.bio && (
              <div className="relative">
                <div className="relative p-6 rounded-2xl bg-white border border-gray-200 shadow-md">
                  <h2 className="text-xl font-bold mb-4">Over dit bedrijf</h2>
                  <p className="text-[#0F172A] leading-relaxed whitespace-pre-wrap break-words">
                    {company.bio}
                  </p>
                </div>
              </div>
            )}

            {/* Social Media */}
            {company.socialMedia && Object.keys(company.socialMedia).length > 0 && (
              <div className="relative">
                <div className="relative p-6 rounded-2xl bg-white border border-gray-200 shadow-md">
                  <h2 className="text-xl font-bold mb-4">Social Media</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                    {Object.entries(company.socialMedia).map(([platform, data]) => {
                      if (!data.username && !data.url) return null
                      const IconComponent = getSocialIcon(platform)
                      return (
                        <a
                          key={platform}
                          href={data.url || `https://${platform}.com/${data.username}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 min-h-[44px] rounded-lg bg-white hover:border-[#3EB489] transition border border-gray-200"
                        >
                          <IconComponent className="w-5 h-5 text-[#3EB489] flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="font-medium capitalize">{platform}</p>
                            {data.followers && (
                              <p className="text-sm text-[#475569]">{data.followers} volgers</p>
                            )}
                          </div>
                        </a>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Managed Brands (Agency only) */}
            {company.role === 'agency' && company.managedBrands && company.managedBrands.length > 0 && (
              <div className="relative">
                <div className="relative p-6 rounded-2xl bg-white border border-gray-200 shadow-md">
                  <h2 className="text-xl font-bold mb-4">Beheerde Merken</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                    {company.managedBrands.map((brand, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3 p-3 rounded-lg bg-[#F8FAFC] border border-gray-200"
                      >
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#3EB489] to-[#2A7E5F] flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-sm font-bold">
                            {(typeof brand === 'string' ? brand : brand.name || '').charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <span className="font-medium text-sm truncate">
                          {typeof brand === 'string' ? brand : brand.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Card */}
            <div className="relative">
              <div className="relative p-6 rounded-2xl bg-white border border-gray-200 shadow-md">
                <h3 className="text-lg font-bold mb-4">Contact</h3>

                <button
                  onClick={handleStartChat}
                  disabled={startingChat}
                  className="w-full mb-4 px-6 py-3 min-h-[48px] bg-[#3EB489] rounded-xl font-semibold hover:bg-[#359E78] transition inline-flex items-center justify-center text-white disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  {startingChat ? 'Chat starten...' : 'Stuur een bericht'}
                </button>

                {company.website && (
                  <a
                    href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full px-6 py-3 min-h-[48px] bg-white border border-gray-200 rounded-xl font-semibold hover:border-[#3EB489] transition inline-flex items-center justify-center text-[#0F172A]"
                  >
                    <Globe className="w-4 h-4 mr-2" />
                    Website bezoeken
                    <ExternalLink className="w-3 h-3 ml-2" />
                  </a>
                )}
              </div>
            </div>

            {/* Company Info Card */}
            <div className="relative">
              <div className="relative p-6 rounded-2xl bg-white border border-gray-200 shadow-md">
                <h3 className="text-lg font-bold mb-4">Bedrijfsinfo</h3>
                <div className="space-y-3">
                  {company.industry && (
                    <div className="flex justify-between gap-4">
                      <span className="text-[#475569] flex-shrink-0">Industrie</span>
                      <span className="font-medium text-right break-words min-w-0">{company.industry}</span>
                    </div>
                  )}
                  {company.companySize && (
                    <div className="flex justify-between gap-4">
                      <span className="text-[#475569] flex-shrink-0">Bedrijfsgrootte</span>
                      <span className="font-medium text-right break-words min-w-0">{company.companySize}</span>
                    </div>
                  )}
                  {formatLocation(company.location) && (
                    <div className="flex justify-between gap-4">
                      <span className="text-[#475569] flex-shrink-0">Locatie</span>
                      <span className="font-medium text-right break-words min-w-0">{formatLocation(company.location)}</span>
                    </div>
                  )}
                  {company.joinedAt && (
                    <div className="flex justify-between gap-4">
                      <span className="text-[#475569] flex-shrink-0">Lid sinds</span>
                      <span className="font-medium text-right">{formatDate(company.joinedAt)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CompanyDetailPage